/**
 * DEV-ONLY fixtures for the Finance dashboard redesign.
 *
 * Reconstructed 1:1 from production screenshots so the three finance pages
 * (dashboard, employee-info, group salary-info) render exactly like prod while
 * working fully offline. Activated only when VUE_APP_FINANCE_MOCK === "true"
 * via src/dev/financeMock.js — never bundled in production builds.
 *
 * Numbers use a seeded PRNG so values stay stable across hot reloads.
 */

// ---------------------------------------------------------------------------
// Seeded PRNG (stable across reloads — no Date.now()/Math.random() drift)
// ---------------------------------------------------------------------------
let _seed = 987654321;
const rnd = () => {
  _seed = (_seed * 1103515245 + 12345) & 0x7fffffff;
  return _seed / 0x7fffffff;
};
const randInt = (a, b) => a + Math.floor(rnd() * (b - a + 1));
const pick = (arr) => arr[Math.floor(rnd() * arr.length)];

// ---------------------------------------------------------------------------
// Branches  ->  GET /company
// Latin titles show in the header branch selector + employee-info groups table.
// The dashboard rows override ids 2/3/4/6/7/16/17 with Cyrillic via
// src/modules/Finance/shared/branchTitle.js — matching prod exactly.
// ---------------------------------------------------------------------------
export const branches = [
  { id: 2, title: "Yunusabad" },
  { id: 3, title: "Tinchlik" },
  { id: 4, title: "Chilanzar" },
  { id: 6, title: "Sergeli" },
  { id: 7, title: "GORKIY" },
  { id: 16, title: "Minor" },
  { id: 17, title: "OYBEK" },
  { id: 20, title: "JR Online" },
];

const BRANCH_IDS = [2, 3, 4, 6, 7, 16, 17];

// Course categories (drive the dashboard "course salary" cards)
const COURSES = [
  "[New] Front-End",
  "[New] Back-End",
  "[New] Front-End PRO",
  "[New] Back-End PRO",
  "Individual Front-End",
  "[New] Beginner",
  "[New] IT Kids",
  "3 qavat ( Pro )",
  "Front-End (React)",
  "[New] Rocket",
  "[New] Front-End Practice",
  "[New] IT Kids PRO",
  "Individual Kids",
  "Back-End (TG Bot & SQL)",
  "CyberSecurity",
  "Python",
  "Web online",
];

const coursePrefix = (course) => {
  if (/Back/i.test(course)) return "nBG";
  if (/Kids/i.test(course)) return "nK";
  if (/Cyber/i.test(course)) return "nCS";
  if (/Python/i.test(course)) return "nPy";
  if (/Individual/i.test(course)) return "IND-F";
  return "nF";
};

let _gid = 3000;
const mkGroup = (branchId, salary, course) => ({
  id: _gid++,
  group: `${coursePrefix(course)}-${randInt(100, 499)}`,
  salary: Math.max(0, Math.round(salary)),
  branch_id: branchId,
  category_name: course,
});

// Split a per-branch total into 1–3 groups so course cards aggregate richly.
const splitBranch = (branchId, total) => {
  if (total <= 0) return [mkGroup(branchId, 0, pick(COURSES))];
  const n = randInt(1, 3);
  const groups = [];
  let remaining = total;
  for (let i = 0; i < n; i++) {
    const course = pick(COURSES);
    const salary = i === n - 1 ? remaining : Math.round(remaining * (0.25 + rnd() * 0.45));
    remaining -= salary;
    groups.push(mkGroup(branchId, salary, course));
  }
  return groups;
};
const mkGroups = (branchAmounts) =>
  Object.entries(branchAmounts).flatMap(([b, t]) => splitBranch(Number(b), t));

const sumGroups = (groups) => groups.reduce((s, g) => s + (g.salary || 0), 0);

// ---------------------------------------------------------------------------
// Named mentors — exact rows + per-branch sums from dashboard screenshot.
// TheEmployee renders:  {index}.{last_name} {first_name} {middle_name}
// ---------------------------------------------------------------------------
const NAMED = [
  { id: 1501, last_name: "Ertan", first_name: "Emirxan", middle_name: "Xxx", grade: "senior", branches: { 2: 14957815 } },
  { id: 1502, last_name: "Tukfatullin", first_name: "Timur", middle_name: "Oskarovich", grade: "middle", branches: { 16: 1115500, 17: 12500, 20: 12500 } },
  { id: 1503, last_name: "Ashurov", first_name: "Nodirbek", middle_name: "Alisher ogli", grade: "junior", branches: { 17: 0 } },
  { id: 1504, last_name: "Xakimov", first_name: "Muxammadaziz", middle_name: "Ravshanjon ogli", grade: "middle", branches: { 6: 7426249 } },
  { id: 1505, last_name: "Haqijonov", first_name: "Mavlonjon", middle_name: "Xosiljon ogli", grade: "junior", branches: { 16: 3490591 } },
  { id: 1506, last_name: "Mirzaaliyev", first_name: "Bekzod", middle_name: "Tashmurod ogli", grade: "senior", branches: { 2: 16377982 } },
  { id: 1507, last_name: "Mordayev", first_name: "Akmaljon", middle_name: "Turgun ogli", grade: "senior", branches: { 2: 3253076, 6: 9563842 } },
  { id: 1508, last_name: "Yusupov", first_name: "Sunnatbek", middle_name: "Odiljon ogli", grade: "senior", branches: { 2: 13324370 } },
  { id: 1509, last_name: "Shermatov", first_name: "Dilbek", middle_name: "Azizbek ogli", grade: "junior", branches: { 6: 1476944 } },
  { id: 1510, last_name: "Yormatov", first_name: "Abdulaziz", middle_name: "Gayratjon ogli", grade: "senior", branches: { 2: 14712720 } },
  { id: 1511, last_name: "Eshmuminov", first_name: "Javohir", middle_name: "Hamid ogli", grade: "middle", branches: { 3: 328986, 16: 518400, 17: 618092 } },
];

// ---------------------------------------------------------------------------
// Elnora Kamolova (id 1549) — employee-info screenshot, 10 groups, all GORKIY.
// Group nF-196 carries id 2814 so the click-through to salary-info works.
// ---------------------------------------------------------------------------
// students / total_attend / total_paid_attendance per group come from the
// group's full_data in /users/salary (here flattened for the list view).
// total_paid_attendance ≈ salary ÷ (stavka ÷ lessons) so the row stays
// self-consistent: Salary = (Stavka ÷ 13) × To'langan. attend ≤ paid.
const ELNORA_GROUPS = [
  { id: 2814, group: "nF-196", salary: 365544, branch_id: 7, category_name: "[New] Front-End", students: 12, total_attend: 29, total_paid_attendance: 36 },
  { id: 2815, group: "nBG-266", salary: 40616, branch_id: 7, category_name: "[New] Back-End", students: 6, total_attend: 3, total_paid_attendance: 4 },
  { id: 2816, group: "nBG-284", salary: 121848, branch_id: 7, category_name: "[New] Back-End", students: 9, total_attend: 10, total_paid_attendance: 12 },
  { id: 2817, group: "nF-293", salary: 71078, branch_id: 7, category_name: "[New] Front-End", students: 7, total_attend: 6, total_paid_attendance: 7 },
  { id: 2818, group: "nBG-324", salary: 50770, branch_id: 7, category_name: "[New] Back-End", students: 8, total_attend: 4, total_paid_attendance: 5 },
  { id: 2819, group: "nBG-339", salary: 528000, branch_id: 7, category_name: "[New] Back-End", students: 11, total_attend: 44, total_paid_attendance: 52 },
  { id: 2820, group: "nF-355", salary: 182772, branch_id: 7, category_name: "[New] Front-End", students: 10, total_attend: 15, total_paid_attendance: 18 },
  { id: 2821, group: "nBG-383", salary: 1096614, branch_id: 7, category_name: "[New] Back-End", students: 14, total_attend: 92, total_paid_attendance: 108 },
  { id: 2822, group: "IND-F-403", salary: 1195000, branch_id: 7, category_name: "Individual Front-End", students: 1, total_attend: 7, total_paid_attendance: 8 },
  { id: 2823, group: "IND-F-404", salary: 1195000, branch_id: 7, category_name: "Individual Front-End", students: 1, total_attend: 7, total_paid_attendance: 8 },
];

const ELNORA = {
  id: 1549,
  first_name: "Elnora",
  last_name: "Kamolova",
  middle_name: "",
  grade: "junior",
  students: 53,
  total_salary: sumGroups(ELNORA_GROUPS), // 4,847,242
  groups: ELNORA_GROUPS,
};

// ---------------------------------------------------------------------------
// Filler mentors — fatten the course cards / table so the redesign is tested
// against a realistically dense dataset (long names, multi-branch, many rows).
// ---------------------------------------------------------------------------
const FIRST = ["Jasur", "Aziz", "Kamola", "Dilnoza", "Sardor", "Nilufar", "Bobur", "Madina", "Otabek", "Sevara", "Ulugbek", "Gulnora", "Sherzod", "Malika", "Rustam", "Zarina", "Doston", "Feruza", "Shoxrux", "Kamron", "Laziz", "Munisa", "Diyor", "Oybek", "Asror"];
const LAST = ["Karimov", "Tursunov", "Ergasheva", "Yusupova", "Rahimov", "Abdullayeva", "Soliyev", "Nazarova", "Qodirov", "Islomova", "Toirov", "Saidova", "Umarov", "Yoqubova", "Aliyev", "Mahmudova", "Bekov", "Komilova", "Raxmonov", "Sobirova"];
const MIDDLE = ["Akmal ogli", "Botir ogli", "Sardor qizi", "Rustam ogli", "Olim qizi", "Anvar ogli", "Davron qizi", "Shavkat ogli"];

const buildFiller = (count) => {
  const out = [];
  for (let i = 0; i < count; i++) {
    const nBranches = randInt(1, 3);
    const used = new Set();
    const branchAmounts = {};
    for (let b = 0; b < nBranches; b++) {
      let bid = pick(BRANCH_IDS);
      let guard = 0;
      while (used.has(bid) && guard++ < 10) bid = pick(BRANCH_IDS);
      used.add(bid);
      branchAmounts[bid] = randInt(3, 180) * 100000; // 300k – 18M
    }
    const groups = mkGroups(branchAmounts);
    out.push({
      id: 1600 + i,
      first_name: pick(FIRST),
      last_name: pick(LAST),
      middle_name: pick(MIDDLE),
      grade: pick(["junior", "middle", "senior"]),
      students: randInt(15, 90),
      total_salary: sumGroups(groups),
      groups,
    });
  }
  return out;
};

const namedEmployees = NAMED.map((m) => {
  const groups = mkGroups(m.branches);
  return {
    id: m.id,
    first_name: m.first_name,
    last_name: m.last_name,
    middle_name: m.middle_name,
    grade: m.grade,
    students: randInt(20, 80),
    total_salary: Object.values(m.branches).reduce((s, v) => s + v, 0),
    groups,
  };
});

export const employees = [...namedEmployees, ELNORA, ...buildFiller(26)];

// Fast lookup: group id -> { group, salary }
const groupById = {};
employees.forEach((e) => (e.groups || []).forEach((g) => (groupById[g.id] = g)));

// ---------------------------------------------------------------------------
// Teacher ratings  ->  GET /reports/teacher/ratings
// ---------------------------------------------------------------------------
export const ratings = [
  // Elnora — real components from reports_teacherscore (May 2026): audit 50 + manual_student_result 30 = 80
  { teacher_id: 1549, order: 35, overall: 80, audit: 50, academy: 0, student_space_usage_score: 30 },
  ...employees
    .filter((e) => e.id !== 1549)
    .map((e, i) => {
      const overall = randInt(55, 98);
      const audit = Math.min(50, overall - randInt(0, 20));
      return {
        teacher_id: e.id,
        order: i + 1,
        overall,
        audit,
        academy: 0,
        student_space_usage_score: overall - audit,
      };
    }),
];

// ---------------------------------------------------------------------------
// Per-group ratings  ->  GET /reports/teacher/group-ratings
// Mirrors reports_teachergroupscore: each group's bonus is scaled by ITS score.
// Elnora demos the user scenario: nBG-284 sits at 79 while others are higher,
// so ONLY that group's bonus is paid at 79%.
// ---------------------------------------------------------------------------
const NEW_RE = /^\[New\]/i;

const ELNORA_GROUP_RATINGS = {
  2814: { audit: 50, students: 30 },  // nF-196 — matches the prod screenshot rate (132 000)
  2815: { audit: 50, students: 45 },  // nBG-266
  2816: { audit: 41, students: 38 },  // nBG-284 — the "79" group from the task
  2817: { audit: 50, students: 42 },  // nF-293
  2818: { audit: 48, students: 40 },  // nBG-324
  2819: { audit: 50, students: 50 },  // nBG-339 — perfect 100
  2820: { audit: 50, students: 35 },  // nF-355
  2821: { audit: 50, students: 44 },  // nBG-383
  2822: { audit: 50, students: 50 },  // IND-F-403 — flat50 (no [New] content)
  2823: { audit: 50, students: 50 },  // IND-F-404 — flat50
};

const groupRatingsByTeacher = {};
employees.forEach((e) => {
  const base = e.id === 1549 ? null : randInt(60, 98);
  groupRatingsByTeacher[e.id] = (e.groups || []).map((g) => {
    const isNew = NEW_RE.test(g.category_name || "");
    let audit;
    let students;
    if (e.id === 1549 && ELNORA_GROUP_RATINGS[g.id]) {
      ({ audit, students } = ELNORA_GROUP_RATINGS[g.id]);
    } else if (!isNew) {
      // Decision #2: courses without scorable [New] content get a flat 50 —
      // only the audit part moves their rating.
      audit = Math.max(0, Math.min(50, base - randInt(0, 6)));
      students = 50;
    } else {
      audit = Math.max(0, Math.min(50, 50 - randInt(0, 9)));
      students = Math.max(0, Math.min(50, base - audit + randInt(-6, 6)));
    }
    return {
      group_id: g.id,
      group_name: g.group,
      category_name: g.category_name,
      audit,
      student_score: students,
      students_flat50: !isNew,
      manual_student_result: null,
      overall: Math.round((audit + students) * 100) / 100,
    };
  });
});

export const getGroupRatings = (teacherId) =>
  groupRatingsByTeacher[Number(teacherId)] || [];

// Keep the legacy teacher-wide rating consistent with the new system:
// aggregate = plain average of the per-group rows (backend decision #3).
const avg = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);
ratings.forEach((r) => {
  const rows = groupRatingsByTeacher[r.teacher_id];
  if (!rows || !rows.length) return;
  r.audit = Math.round(avg(rows.map((x) => x.audit)) * 100) / 100;
  r.student_space_usage_score = Math.round(avg(rows.map((x) => x.student_score)) * 100) / 100;
  r.overall = Math.round(avg(rows.map((x) => x.overall)) * 100) / 100;
});
ratings.sort((a, b) => b.overall - a.overall).forEach((r, i) => (r.order = i + 1));

// group id -> its per-group rating row (for the salary-info page rating block)
const groupRatingById = {};
Object.values(groupRatingsByTeacher).forEach((rows) =>
  rows.forEach((r) => (groupRatingById[r.group_id] = r))
);
export const getGroupRating = (gid) => groupRatingById[Number(gid)] || null;

// full_data.rating block exactly as the backend salary loop writes it
const ratingBlock = (gid) => {
  const r = groupRatingById[Number(gid)];
  if (!r) return { overall: null, source: "none", audit: null, students: null, students_flat50: null };
  return {
    overall: r.overall,
    source: "group",
    audit: r.audit,
    students: r.student_score,
    students_flat50: r.students_flat50,
  };
};

// ---------------------------------------------------------------------------
// Recalculation history  ->  GET /users/salary/recalculation-history
// ---------------------------------------------------------------------------
export const recalcInfo = {
  recalculated_at: "2026-06-01T10:32:00",
  recalculated_by: { name: "Murad Finance" },
};

// ---------------------------------------------------------------------------
// Group salary detail  ->  GET /finance/salary/group/{id}
// ---------------------------------------------------------------------------
const GROUP_2814_STUDENTS = [
  ["Bekmurodov", "Shoxjahon", 2, 1],
  ["Qo'chqoriv", "Shuxratbek", 3, 0],
  ["Janbolat", "Agabekov", 3, 0],
  ["Samandarova", "Charosxon", 2, 1],
  ["Raximov", "Ibroxim", 2, 1],
  ["Bozorboyeva", "Kumush", 2, 1],
  ["Abduali", "Abdurazzoqov", 3, 0],
  ["Eshmominov", "Bilol", 3, 0],
  ["G'ayratova", "Xushnura", 2, 1],
  ["Yoldoshev", "Ibrohim", 2, 1],
  ["Foziljon", "Abdumutalo'v", 2, 1],
  ["Xolmurodov", "Bahodir", 3, 0],
];

const GROUP_2814 = {
  group: "nF-196",
  category_name: "[New] Front-End",
  payment_per_student: 132000, // stavka: base 100000 + 40000 * (group rating 80/100)
  rating: { overall: 80, source: "group", audit: 50, students: 30, students_flat50: false },
  group_salary: 365544,
  lessons: 13,
  total_lessons: 156,
  total_attend: 29,
  total_not_attend: 7,
  total_paid_attendance: 36,
  students_attendances_detail: GROUP_2814_STUDENTS.map((s, i) => ({
    student_id: 100 + i,
    first_name: s[0],
    last_name: s[1],
    attend: s[2],
    not_attend: s[3],
    paid_attendance: 3,
    payment_for_student: 30462, // 3 * round(132000 / 13) = 3 * 10154
  })),
};

const FILLER_FIRST = ["Bekmurodov", "Qodirov", "Tursunova", "Aliyev", "Saidova", "Rahimov", "Yusupova", "Karimov", "Ergasheva", "Soliyev", "Nazarova", "Umarov", "Islomova", "Toirov"];
const FILLER_LAST = ["Shoxjahon", "Diyor", "Madina", "Sardor", "Nilufar", "Bobur", "Sevara", "Otabek", "Gulnora", "Sherzod", "Malika", "Rustam", "Zarina", "Doston"];

// Build a believable attendance detail for any other group from its salary.
const buildGroupDetail = (gid) => {
  const g = groupById[gid];
  const target = g?.salary || 0;
  const nStudents = randInt(8, 14);
  const lessons = randInt(10, 13);

  // Generate attendance first, then pick a per-student rate so the group total
  // lands near the real figure and every row stays internally consistent
  // (summa === paid_attendance × per-lesson).
  const rows = [];
  let sumPaid = 0;
  let totalAttend = 0;
  let totalNot = 0;
  for (let i = 0; i < nStudents; i++) {
    const attend = randInt(8, lessons);
    const notAttend = randInt(0, 3);
    const paid = Math.min(lessons, attend + Math.min(notAttend, 3));
    rows.push({ attend, notAttend, paid });
    sumPaid += paid;
    totalAttend += attend;
    totalNot += notAttend;
  }
  const pps = sumPaid > 0 ? Math.round((target * lessons) / sumPaid) : 0;
  const perLesson = lessons > 0 ? pps / lessons : 0;

  let groupSalary = 0;
  let totalPaid = 0;
  const detail = rows.map((r, i) => {
    const pay = Math.round(r.paid * perLesson);
    groupSalary += pay;
    totalPaid += r.paid;
    return {
      student_id: gid * 100 + i,
      first_name: pick(FILLER_FIRST),
      last_name: pick(FILLER_LAST),
      attend: r.attend,
      not_attend: r.notAttend,
      paid_attendance: r.paid,
      payment_for_student: pay,
    };
  });

  return {
    group: g?.group || "",
    category_name: g?.category_name || "",
    payment_per_student: pps,
    rating: ratingBlock(gid),
    lessons,
    group_salary: groupSalary,
    total_lessons: lessons * nStudents,
    total_attend: totalAttend,
    total_not_attend: totalNot,
    total_paid_attendance: totalPaid,
    students_attendances_detail: detail,
  };
};

export const getGroupDetail = (gid) => {
  if (Number(gid) === 2814) return GROUP_2814;
  return buildGroupDetail(Number(gid));
};

// ---------------------------------------------------------------------------
// Deep-link helpers (used by the dev mock to seed localStorage when an
// employee-info / salary-info page is opened directly instead of via a click).
// ---------------------------------------------------------------------------
export const findEmployee = (userId) =>
  employees.find((e) => String(e.id) === String(userId)) || null;

// group id -> { group_name, teacher_id }
const groupOwner = {};
employees.forEach((e) =>
  (e.groups || []).forEach((g) => (groupOwner[g.id] = { group_name: g.group, teacher_id: e.id }))
);
export const getGroupMeta = (gid) => groupOwner[Number(gid)] || null;

// ---------------------------------------------------------------------------
// Per-course rate config  ->  GET /finance/course-rates
// These columns already exist on the backend GroupCategory model
// (base_payment / payment_for_junior|middle|senior / has_rating) — the demo
// surfaces them so the UI can show "fix + bonus = rate" per course.
// ---------------------------------------------------------------------------
// Real production values pulled from courses_groupcategory (2026-06).
export const courseRates = [
  { category_name: "[New] Front-End", base_payment: 100000, payment_for_junior: 40000, payment_for_middle: 70000, payment_for_senior: 110000, has_rating: true },
  { category_name: "[New] Back-End", base_payment: 100000, payment_for_junior: 40000, payment_for_middle: 70000, payment_for_senior: 110000, has_rating: true },
  { category_name: "[New] Front-End PRO", base_payment: 100000, payment_for_junior: 70000, payment_for_middle: 110000, payment_for_senior: 150000, has_rating: true },
  { category_name: "[New] Back-End PRO", base_payment: 100000, payment_for_junior: 70000, payment_for_middle: 110000, payment_for_senior: 150000, has_rating: true },
  { category_name: "[New] Beginner", base_payment: 100000, payment_for_junior: 40000, payment_for_middle: 70000, payment_for_senior: 110000, has_rating: true },
  { category_name: "[New] Rocket", base_payment: 100000, payment_for_junior: 40000, payment_for_middle: 70000, payment_for_senior: 110000, has_rating: true },
  { category_name: "[New] IT Kids", base_payment: 80000, payment_for_junior: 15000, payment_for_middle: 35000, payment_for_senior: 65000, has_rating: true },
  { category_name: "[New] IT Kids PRO", base_payment: 80000, payment_for_junior: 15000, payment_for_middle: 35000, payment_for_senior: 65000, has_rating: true },
  { category_name: "[New] Front-End Practice", base_payment: 100000, payment_for_junior: 120000, payment_for_middle: 160000, payment_for_senior: 200000, has_rating: true },
  { category_name: "Individual Front-End", base_payment: 1195000, payment_for_junior: 0, payment_for_middle: 0, payment_for_senior: 0, has_rating: true },
  { category_name: "Individual Kids", base_payment: 790000, payment_for_junior: 0, payment_for_middle: 0, payment_for_senior: 0, has_rating: true },
  { category_name: "Front-End (React)", base_payment: 150000, payment_for_junior: 150000, payment_for_middle: 180000, payment_for_senior: 200000, has_rating: true },
  { category_name: "3 qavat ( Pro )", base_payment: 280000, payment_for_junior: 280000, payment_for_middle: 300000, payment_for_senior: 320000, has_rating: true },
  { category_name: "CyberSecurity", base_payment: 100000, payment_for_junior: 40000, payment_for_middle: 70000, payment_for_senior: 110000, has_rating: true },
  { category_name: "Back-End (TG Bot & SQL)", base_payment: 150000, payment_for_junior: 150000, payment_for_middle: 180000, payment_for_senior: 200000, has_rating: true },
  { category_name: "Web Minor 2ч", base_payment: 100000, payment_for_junior: 0, payment_for_middle: 0, payment_for_senior: 0, has_rating: true },
  { category_name: "Python", base_payment: 150000, payment_for_junior: 150000, payment_for_middle: 180000, payment_for_senior: 200000, has_rating: true },
  { category_name: "Web online", base_payment: 100000, payment_for_junior: 0, payment_for_middle: 0, payment_for_senior: 0, has_rating: true },
];

const ratesByName = {};
courseRates.forEach((c) => (ratesByName[c.category_name] = c));
// Fallback so any course still renders a plausible rate breakdown.
export const getCourseRate = (name) =>
  ratesByName[name] || {
    category_name: name,
    base_payment: 100000,
    payment_for_junior: 40000,
    payment_for_middle: 70000,
    payment_for_senior: 110000,
    has_rating: true,
  };
